import { Children } from 'react';
import { Button as MUIButton, ButtonProps as MUIButtonProps } from '@mui/material';
import {
  AB3, AR2, AR4, P3, P4, S1, S2, S3, S4,
} from '../../theme';
import Box from '../Box';
import Spinner from '../../animations/Spinner';
import { TYPOGRAPHY_SUBTITLE1 } from '../Typography';
import { mergeSX } from '../../services/helpers';

export interface ButtonProps extends MUIButtonProps {
  isLoading?: boolean;
  icon?: object;
  disableMinWidth?: boolean;
  disablePadding?: boolean;
  padding?: string | number;
  borderRadius?: string | number;
}

interface IBuildSXParams {
  disableMinWidth: boolean;
  disablePadding: boolean;
  isLoading?: boolean;
  padding?: string | number;
  borderRadius?: string | number;
}

const buildSX = ({
  disablePadding, padding, disableMinWidth, borderRadius, isLoading,
}: IBuildSXParams) => ({
  '&.MuiButton-root': {
    ...TYPOGRAPHY_SUBTITLE1,
    padding: disablePadding ? 0 : (padding ?? '13px 24px'),
    borderRadius: borderRadius ?? '16px',
    boxShadow: 'none',
    minWidth: disableMinWidth ? 'unset' : 134,
    height: 48,
    textTransform: 'none',
    '& svg': {
      stroke: S1,
    },
    '&:hover': {
      boxShadow: 'none',
      backgroundColor: S2,
      color: AB3,
      '& svg': {
        stroke: AB3,
      },
      '&.MuiButton-containedError': {
        backgroundColor: AR2,
        color: S1,
      },
    },
    '&:active': {
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: S3,
      color: S4,
      '& svg': {
        stroke: S4,
      },
      '&.MuiButton-contained': {
        backgroundColor: isLoading ? P4 : S3,
        '& svg': {
          stroke: S1,
          color: isLoading ? S1 : 'inherit',
        },
        '&.MuiButton-containedError': {
          borderColor: S2,
          backgroundColor: S1,
          color: `${AR2}66`,
          '& svg': {
            stroke: AR2,
            color: isLoading ? AR2 : 'inherit',
          },
        },
      },
      '&.MuiButton-outlined': {
        border: isLoading ? `2px solid ${AB3}` : `2px solid ${S3}`,
        backgroundColor: S1,
        '& svg': {
          stroke: AB3,
          color: isLoading ? AB3 : 'inherit',
        },
      },
      '&.MuiButton-text': {
        color: S4,
        backgroundColor: S1,
        '& svg': {
          stroke: AB3,
        },
      },
    },
    '&.MuiButton-text': {
      color: AB3,
      '&:hover': {
        backgroundColor: 'rgba(0,0,0,0.05)',
      },
    },
  },
  '&.MuiButton-contained': {
    backgroundColor: AB3,
  },
  '&.MuiButton-containedError': {
    backgroundColor: AR4,
    color: AR2,
    border: `2px solid ${AR2}`,
  },
  '&.MuiButton-outlined': {
    color: AB3,
    '& svg': {
      stroke: AB3,
    },
    border: `2px solid ${AB3}`,
    '&:hover': {
      border: `2px solid ${P3}`,
      color: P3,
      '& svg': {
        stroke: P3,
      },
    },
  },
});

const Button = (props: ButtonProps) => {
  const {
    children,
    isLoading,
    variant = 'contained',
    icon,
    disabled,
    disableMinWidth = false,
    disablePadding = false,
    padding,
    borderRadius,
    sx,
    ...restProps
  } = props;

  /*
    If we translate a page using on-page Google Translate - it will wrap string elements with
    a 'font' tag; after 'isLoading' changes, we will get an error:
      "Failed to execute 'removeChild' on 'Node'"
    To fix this issue, we should wrap every string element with a 'span' element.
    For more information:
      https://uniqkey.atlassian.net/browse/NEXTGEN-7020
      https://github.com/facebook/react/issues/11538#issuecomment-390386520
  */
  const processedChildren = Children.map(
    children,
    (child) => (typeof child === 'string' ? <span>{child}</span> : child),
  );

  const processedSX = mergeSX(buildSX({
    disablePadding, padding, disableMinWidth, borderRadius, isLoading,
  }), sx);

  if (!icon) {
    return (
      <MUIButton
        sx={processedSX}
        variant={variant}
        disabled={isLoading || disabled}
        {...restProps}
      >
        {isLoading ? <Spinner /> : processedChildren}
      </MUIButton>
    );
  }

  return (
    <MUIButton
      sx={processedSX}
      variant={variant}
      disabled={isLoading || disabled}
      {...restProps}
    >
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {icon}
          <Box mr={1} />
          {processedChildren}
        </>
      )}
    </MUIButton>
  );
};

export default Button;
